"use client";

import React, { useState, useEffect } from "react";
import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import {
  ShieldCheck,
  CheckCircle2,
  Sparkles,
  Lock,
  Mail,
  User,
  Building,
  Phone,
  ArrowRight,
  RefreshCw,
  AlertCircle,
  MessageSquare,
  Clock,
  Eye,
  EyeOff,
  Zap
} from "lucide-react";
import { Button } from "@/components/ui/Button";

const INDUSTRIES = [
  { id: "trading", name: "Trading, Commodities & Import/Export", desc: "Commodity RFQs, Incoterms, container volume & shipment tracking" },
  { id: "manufacturing", name: "Manufacturing & Industrial Supplies", desc: "Batch RFQs, CNC specs, component drawings & PO status" },
  { id: "real-estate", name: "Real Estate Brokerage / Agency", desc: "Off-plan brochures, WhatsApp speed-to-lead & VIP site tours" },
  { id: "logistics", name: "Logistics, Freight & Warehousing", desc: "Cargo rate quotes & consignment dispatch alerts" },
  { id: "dental", name: "Dental Clinic / Healthcare Practice", desc: "Instant appointment booking & treatment follow-ups" },
  { id: "gym", name: "Fitness Center / CrossFit Gym", desc: "Trial pass conversion & personal trainer booking" },
  { id: "legal", name: "Law Firm / CA / Legal Advisory", desc: "Retainer inquiries & case evaluation tracking" },
  { id: "restaurant", name: "Restaurant / Banquet Hospitality", desc: "VIP table reservations & event catering inquiries" },
  { id: "education", name: "Education, Institutes & EdTech", desc: "Course inquiries, demo lectures & student admissions" },
  { id: "insurance", name: "Insurance & Wealth Advisory", desc: "Policy quotes, fleet insurance & term renewal follow-ups" },
  { id: "telecalling", name: "BPO, Telecalling & Sales Outbound", desc: "Sub-15s dialer & multi-agent lead distribution" },
  { id: "general", name: "Professional Services / SaaS / Other", desc: "General multi-stage pipeline & webhook automation" },
];

export default function RegisterClient() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const selectedPlan = searchParams.get("plan");
  const urlIndustry = searchParams.get("industry");

  // Step 1: Form Fields
  const [fullName, setFullName] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [industry, setIndustry] = useState(() => {
    if (urlIndustry && INDUSTRIES.some((i) => i.id === urlIndustry)) {
      return urlIndustry;
    }
    return "real-estate";
  });
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  // States
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Step 2: Verification Modal
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [registeredUserId, setRegisteredUserId] = useState<number | null>(null);
  const [assignedUsername, setAssignedUsername] = useState("");
  const [maskedPhone, setMaskedPhone] = useState("");
  const [maskedEmail, setMaskedEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpError, setOtpError] = useState("");
  const [simulatedOtp, setSimulatedOtp] = useState<string | null>(null);
  const [resendCooldown, setResendCooldown] = useState(60);
  const [lockoutSeconds, setLockoutSeconds] = useState(0);
  const [resendSuccess, setResendSuccess] = useState("");

  // Cooldown countdown timer
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (resendCooldown > 0 && showOtpModal) {
      timer = setInterval(() => setResendCooldown((prev) => prev - 1), 1000);
    }
    return () => clearInterval(timer);
  }, [resendCooldown, showOtpModal]);

  // Lockout countdown timer
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (lockoutSeconds > 0) {
      timer = setInterval(() => setLockoutSeconds((prev) => Math.max(0, prev - 1)), 1000);
    }
    return () => clearInterval(timer);
  }, [lockoutSeconds]);

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!fullName.trim() || !email.trim() || !phone.trim() || !password.trim()) {
      setError("Please fill out all required fields.");
      return;
    }

    if (!agreedToTerms) {
      setError("You must review and accept the Master Terms of Service, Privacy Policy, and Acceptable Use Policy to start your trial.");
      return;
    }

    if (password.length < 8) {
      setError("Password must be at least 8 characters long.");
      return;
    }

    setLoading(true);

    try {
      const res = await fetch("/api/crm/auth/register", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          fullName,
          companyName,
          email,
          phone,
          industry,
          password,
          planTier: selectedPlan || "starter",
        }),
      });

      const data = await res.json();

      if (res.ok && data.success) {
        setRegisteredUserId(data.userId);
        if (data.username) setAssignedUsername(data.username);
        setMaskedPhone(data.phone || phone);
        if (data.email) setMaskedEmail(data.email);
        if (data.simulated) {
          setSimulatedOtp(data.simulated);
        }

        // Record immutable click-wrap consent audit trail
        try {
          await fetch("/api/crm/consent", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              userId: data.userId,
              documentSlugs: ["terms", "privacy", "refund", "aup"],
              consentType: "signup_trial",
            }),
          });
        } catch (consentErr) {
          console.warn("[Consent Log Warning]:", consentErr);
        }

        setShowOtpModal(true);
        setResendCooldown(60);
      } else {
        setError(data.error || "Registration failed. Please check your details.");
      }
    } catch {
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!otp.trim() || !registeredUserId) {
      setOtpError("Please enter the 6-digit verification code.");
      return;
    }

    setOtpLoading(true);
    setOtpError("");

    try {
      const res = await fetch("/api/crm/auth/verify-phone", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          userId: registeredUserId,
          otp: otp.trim(),
        }),
      });

      const data = await res.json();

      if (res.ok && data.success) {
        // Successfully verified! Redirect to CRM
        router.push("/crm");
      } else {
        if (data.locked && data.waitSeconds) {
          setLockoutSeconds(data.waitSeconds);
        }
        setOtpError(data.error || "Invalid verification code.");
      }
    } catch {
      setOtpError("Network error. Please try again.");
    } finally {
      setOtpLoading(false);
    }
  };

  const handleResendOtp = async (channel: "email" | "sms" | "whatsapp" = "email") => {
    if (!registeredUserId || resendCooldown > 0 || lockoutSeconds > 0) return;

    setOtpLoading(true);
    setOtpError("");
    setResendSuccess("");

    try {
      const res = await fetch("/api/crm/auth/resend-otp", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          userId: registeredUserId,
          channel,
        }),
      });

      const data = await res.json();
      if (res.ok && data.success) {
        if (data.simulated) setSimulatedOtp(data.simulated);
        setResendCooldown(60);
        const channelLabel = channel === "email" ? "your email" : channel === "whatsapp" ? "WhatsApp" : "SMS";
        setResendSuccess(`Fresh code sent to ${channelLabel}!`);
        setTimeout(() => setResendSuccess(""), 6000);
      } else {
        if (data.locked && data.waitSeconds) setLockoutSeconds(data.waitSeconds);
        setOtpError(data.error || "Failed to resend code.");
      }
    } catch {
      setOtpError("Failed to connect to verification server.");
    } finally {
      setOtpLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-white pt-24 pb-20 px-4 sm:px-6 relative overflow-hidden">
      {/* Background ambient lighting */}
      <div className="absolute top-20 left-1/2 -translate-x-1/2 w-full max-w-7xl h-96 bg-gradient-to-tr from-cyan-500/10 via-primary/10 to-indigo-500/10 blur-3xl pointer-events-none rounded-full" />

      <div className="max-w-6xl mx-auto relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          {/* Left Column: Value Proposition */}
          <div className="lg:col-span-5 space-y-6 pt-4">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-cyan-600 dark:text-cyan-400 text-xs font-semibold uppercase tracking-wider">
              <Sparkles className="w-3.5 h-3.5" />
              <span>7-Day Full Access Free Trial</span>
            </div>

            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-tight">
              Test Tier 1 <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-500 to-blue-600">Speed-to-Lead</span> on Your Pipeline.
            </h1>

            <p className="text-sm sm:text-base text-slate-600 dark:text-slate-400 leading-relaxed">
              Stop losing high-value prospects to slow response times. Self-register in under 60 seconds to unlock the official Meta WhatsApp & Webhook CRM.
            </p>

            {/* Feature Checklist */}
            <div className="space-y-3.5 pt-2">
              {[
                "Everything in Tier 1 Plan Included Free for 7 Days",
                "50 Autonomous Meta WhatsApp Automations (<15s Response)",
                "25 Transactional Direct SMS Dispatches Included",
                "10 Simulated AI Voice Qualification Minutes",
                "Pre-seeded realistic demo leads tailored to your industry",
                "Zero credit card required — activate immediately via Phone OTP",
              ].map((feat, idx) => (
                <div key={idx} className="flex items-start gap-3 text-xs sm:text-sm font-medium text-slate-700 dark:text-slate-300">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
                  <span>{feat}</span>
                </div>
              ))}
            </div>

            {/* Testimonial / Trust Metric */}
            <div className="p-5 rounded-2xl bg-white/60 dark:bg-slate-900/60 border border-slate-200/80 dark:border-slate-800/80 backdrop-blur-md shadow-sm">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center text-white font-bold text-sm shadow-md">
                  JS
                </div>
                <div>
                  <h4 className="text-xs font-bold text-slate-900 dark:text-white">Zero Setup SaaS Guarantee</h4>
                  <p className="text-[11px] text-slate-500 dark:text-slate-400">
                    Unlimited team users. No per-seat subscription taxes.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column: Registration Card */}
          <div className="lg:col-span-7">
            <div className="p-8 sm:p-10 rounded-3xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-2xl shadow-primary/5">
              <div className="mb-6">
                <h2 className="text-xl sm:text-2xl font-bold">Create Your Free Trial Account</h2>
                <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 mt-1">
                  Already have access?{" "}
                  <Link href="/crm" className="text-cyan-600 dark:text-cyan-400 hover:underline font-semibold">
                    Sign in to CRM
                  </Link>
                </p>
              </div>

              {error && (
                <div className="mb-6 p-4 rounded-xl bg-red-500/10 border border-red-500/20 text-red-600 dark:text-red-400 text-xs flex items-start gap-2.5">
                  <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                  <div>{error}</div>
                </div>
              )}

              {selectedPlan && (
                <div className="mb-5 p-3.5 rounded-xl bg-cyan-500/10 border border-cyan-500/25 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-cyan-500 shrink-0" />
                    <div>
                      <span className="text-xs font-bold text-slate-900 dark:text-white capitalize block">
                        {selectedPlan} Plan (7-Day Trial)
                      </span>
                      <span className="text-[10px] text-slate-500 dark:text-slate-400">
                        Zero per-seat taxes • Full access unlocked
                      </span>
                    </div>
                  </div>
                  <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-600 dark:text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 px-2.5 py-1 rounded-full">
                    ₹0 / No Card Needed
                  </span>
                </div>
              )}

              <form onSubmit={handleRegister} className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Full Name */}
                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-wider text-slate-700 dark:text-slate-300 mb-1.5">
                      Your Name *
                    </label>
                    <div className="relative">
                      <span className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
                        <User className="w-4 h-4" />
                      </span>
                      <input
                        type="text"
                        required
                        value={fullName}
                        onChange={(e) => setFullName(e.target.value)}
                        placeholder="Alex Morgan"
                        className="w-full pl-10 pr-4 py-2.5 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all"
                      />
                    </div>
                  </div>

                  {/* Company Name */}
                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-wider text-slate-700 dark:text-slate-300 mb-1.5">
                      Business / Brand Name
                    </label>
                    <div className="relative">
                      <span className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
                        <Building className="w-4 h-4" />
                      </span>
                      <input
                        type="text"
                        value={companyName}
                        onChange={(e) => setCompanyName(e.target.value)}
                        placeholder="Apex Properties"
                        className="w-full pl-10 pr-4 py-2.5 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all"
                      />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Work Email */}
                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-wider text-slate-700 dark:text-slate-300 mb-1.5">
                      Work Email *
                    </label>
                    <div className="relative">
                      <span className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
                        <Mail className="w-4 h-4" />
                      </span>
                      <input
                        type="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="alex@company.com"
                        className="w-full pl-10 pr-4 py-2.5 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all"
                      />
                    </div>
                  </div>

                  {/* Mobile Phone */}
                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-wider text-slate-700 dark:text-slate-300 mb-1.5">
                      Mobile / WhatsApp Phone *
                    </label>
                    <div className="relative">
                      <span className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
                        <Phone className="w-4 h-4" />
                      </span>
                      <input
                        type="tel"
                        required
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        placeholder="+1 (555) 234-5678 or +91 98765 43210"
                        className="w-full pl-10 pr-4 py-2.5 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all"
                      />
                    </div>
                  </div>
                </div>

                {/* Industry Vertical Dropdown */}
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-slate-700 dark:text-slate-300 mb-1.5">
                    Select Your Industry Vertical *
                  </label>
                  <select
                    value={industry}
                    onChange={(e) => setIndustry(e.target.value)}
                    className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all"
                  >
                    {INDUSTRIES.map((ind) => (
                      <option key={ind.id} value={ind.id}>
                        {ind.name}
                      </option>
                    ))}
                  </select>
                  <p className="text-[11px] text-cyan-600 dark:text-cyan-400 mt-1 font-medium">
                    ⚡ {INDUSTRIES.find((i) => i.id === industry)?.desc}
                  </p>
                </div>

                {/* Password */}
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-slate-700 dark:text-slate-300 mb-1.5">
                    Account Password *
                  </label>
                  <div className="relative">
                    <span className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
                      <Lock className="w-4 h-4" />
                    </span>
                    <input
                      type={showPassword ? "text" : "password"}
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••••••"
                      className="w-full pl-10 pr-10 py-2.5 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute inset-y-0 right-0 pr-3.5 flex items-center text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                {/* Mandatory Click-Wrap Legal Consent (Module 4) */}
                <div className="pt-2">
                  <label className="flex items-start gap-2.5 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      required
                      checked={agreedToTerms}
                      onChange={(e) => setAgreedToTerms(e.target.checked)}
                      className="mt-1 w-4 h-4 rounded border-slate-300 dark:border-slate-700 text-primary focus:ring-primary cursor-pointer shrink-0"
                    />
                    <span className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
                      I have read, understood, and accept the{" "}
                      <Link href="/terms" target="_blank" className="text-cyan-600 dark:text-cyan-400 hover:underline font-semibold">
                        Terms of Service (v1.0)
                      </Link>
                      ,{" "}
                      <Link href="/privacy" target="_blank" className="text-cyan-600 dark:text-cyan-400 hover:underline font-semibold">
                        Privacy Policy (DPDP 2023 / GDPR)
                      </Link>
                      ,{" "}
                      <Link href="/refund" target="_blank" className="text-cyan-600 dark:text-cyan-400 hover:underline font-semibold">
                        Refund Policy
                      </Link>
                      , and{" "}
                      <Link href="/aup" target="_blank" className="text-cyan-600 dark:text-cyan-400 hover:underline font-semibold">
                        Acceptable Use Policy
                      </Link>
                      .
                    </span>
                  </label>
                </div>

                <Button
                  type="submit"
                  disabled={loading || !agreedToTerms}
                  className="w-full py-3 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 mt-4 cursor-pointer shadow-xl shadow-primary/20 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <>
                      <span>Continue to Phone Verification</span>
                      <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </Button>

                <p className="text-[11px] text-slate-400 dark:text-slate-500 text-center mt-2">
                  Zero credit card required. 7-day full access trial.
                </p>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Step 2: Phone Verification Modal */}
      {showOtpModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-md">
          <div className="w-full max-w-md p-8 rounded-3xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-2xl relative animate-in fade-in zoom-in-95 duration-200">
            <div className="text-center mb-6">
              <div className="w-12 h-12 rounded-2xl bg-cyan-500/10 text-cyan-600 dark:text-cyan-400 flex items-center justify-center mx-auto mb-3">
                <Mail className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-bold">Check Your Email for Code</h3>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                Enter the 6-digit verification code sent to <br />
                <span className="font-semibold text-cyan-600 dark:text-cyan-400">{maskedEmail || email || "your email"}</span>
              </p>
              {assignedUsername && (
                <div className="mt-2.5 inline-flex items-center gap-1.5 px-3 py-1 rounded-lg bg-slate-100 dark:bg-slate-800 text-[11px] text-slate-600 dark:text-slate-300">
                  <span>Assigned Username:</span>
                  <strong className="font-mono text-cyan-600 dark:text-cyan-400">{assignedUsername}</strong>
                </div>
              )}
            </div>

            {simulatedOtp && (
              <div className="mb-4 p-3 rounded-xl bg-amber-500/10 border border-amber-500/20 text-amber-600 dark:text-amber-400 text-xs text-center font-mono">
                🧪 Dev Mode Code: <strong>{simulatedOtp}</strong>
              </div>
            )}

            {lockoutSeconds > 0 && (
              <div className="mb-4 p-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-600 dark:text-red-400 text-xs text-center flex items-center justify-center gap-2">
                <Clock className="w-4 h-4 shrink-0" />
                <span>Verification locked. Retry in {Math.floor(lockoutSeconds / 60)}m {lockoutSeconds % 60}s</span>
              </div>
            )}

            {resendSuccess && (
              <div className="mb-4 p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-600 dark:text-emerald-400 text-xs text-center font-medium flex items-center justify-center gap-2 animate-in fade-in">
                <CheckCircle2 className="w-4 h-4 shrink-0" />
                <span>{resendSuccess}</span>
              </div>
            )}

            {otpError && (
              <div className="mb-4 p-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-600 dark:text-red-400 text-xs text-center">
                {otpError}
              </div>
            )}

            <form onSubmit={handleVerifyOtp} className="space-y-5">
              <div>
                <input
                  type="text"
                  maxLength={6}
                  required
                  autoFocus
                  disabled={lockoutSeconds > 0}
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                  placeholder="123456"
                  className="w-full text-center text-3xl font-extrabold tracking-[0.5em] py-3 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-2xl focus:outline-none focus:ring-2 focus:ring-cyan-500/50"
                />
              </div>

              <Button
                type="submit"
                disabled={otpLoading || lockoutSeconds > 0 || otp.length < 6}
                className="w-full py-3 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 cursor-pointer shadow-lg shadow-cyan-500/20"
              >
                {otpLoading ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <>
                    <span>Verify & Launch 7-Day Trial</span>
                    <Zap className="w-4 h-4" />
                  </>
                )}
              </Button>

              <div className="pt-2 text-center space-y-2.5">
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  Didn't receive the code?
                </p>
                <div className="flex flex-wrap items-center justify-center gap-2">
                  <button
                    type="button"
                    disabled={resendCooldown > 0 || lockoutSeconds > 0}
                    onClick={() => handleResendOtp("email")}
                    className="px-3 py-1.5 rounded-lg bg-cyan-500/10 hover:bg-cyan-500/20 text-xs font-semibold text-cyan-600 dark:text-cyan-400 disabled:opacity-50 cursor-pointer flex items-center gap-1.5 transition-colors"
                  >
                    <Mail className="w-3.5 h-3.5" />
                    <span>Resend to Email {resendCooldown > 0 ? `(${resendCooldown}s)` : ""}</span>
                  </button>
                  <button
                    type="button"
                    disabled={resendCooldown > 0 || lockoutSeconds > 0}
                    onClick={() => handleResendOtp("whatsapp")}
                    className="px-3 py-1.5 rounded-lg bg-emerald-500/10 hover:bg-emerald-500/20 text-xs font-semibold text-emerald-600 dark:text-emerald-400 disabled:opacity-50 cursor-pointer flex items-center gap-1.5 transition-colors"
                  >
                    <MessageSquare className="w-3.5 h-3.5" />
                    <span>WhatsApp</span>
                  </button>
                  <button
                    type="button"
                    disabled={resendCooldown > 0 || lockoutSeconds > 0}
                    onClick={() => handleResendOtp("sms")}
                    className="px-3 py-1.5 rounded-lg bg-slate-500/10 hover:bg-slate-500/20 text-xs font-semibold text-slate-600 dark:text-slate-300 disabled:opacity-50 cursor-pointer flex items-center gap-1.5 transition-colors"
                  >
                    <Phone className="w-3.5 h-3.5" />
                    <span>SMS</span>
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
